// src/firebase-handler.js
import { initializeApp } from 'firebase/app';
import {
    // user
    getAuth,
    signOut,
    deleteUser,
    updateProfile,

    // sign in
    signInWithPopup,
    signInWithCredential,
    fetchSignInMethodsForEmail,
    signInWithRedirect,
    signInWithEmailAndPassword,

    // linking accounts
    getRedirectResult,
    isSignInWithEmailLink,
    linkWithCredential,

    // providers
    EmailAuthProvider,
    GoogleAuthProvider,
    FacebookAuthProvider,
} from 'firebase/auth';


const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
export const providerProfilePics = {
    fb: "",
    google: "",
};

//=============================================
// Google Login
export const signInWithGoogle = async onError => {
    try {
        console.log(`[signInWithGoogle] attempting to login with google`);
        const result = await signInWithPopup(auth, googleProvider);
        console.log(`[signInWithGoogle] Success got the result:`, result);
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential.accessToken;
        console.log(`[signInWithGoogle] got the token: ${token}`);

        const user = result.user;
        console.log(`[signInWithGoogle] User signed in with UID: ${user.uid}`);
    } catch (error) {
        console.error(`[signInWithGoogle] Error: ${error}`);
        if (onError) {
            onError(error.message);
        }
    }
};

//=============================================
// Facebook OAuth redirect
export const loadFacebookSDK = () => {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: process.env.REACT_APP_FB_APP_ID,
        cookie: true,
        xfbml: false,
        version: 'v18.0', // or latest
      });
      resolve(window.FB);
    };

    // Load the SDK script
    (function (d, s, id) {
      var js,
        fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');

    // Timeout reject if SDK fails to load
    setTimeout(() => reject(new Error('FB SDK failed to load')), 10000);
  });
};

export const signInWithFacebookRedirect = () => {
    console.log("[FB] Initiating OAuth redirect login...");

    const fbAppId = process.env.REACT_APP_FB_APP_ID;
    const redirectUri = `${window.location.origin}/__/auth/handler`;
    const scope = "email,public_profile";

    // Build the OAuth URL
    const oauthUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${encodeURIComponent(
        redirectUri
    )}&state=fb_redirect&scope=${encodeURIComponent(scope)}`;

    // Redirect the browser
    window.location.href = oauthUrl;
};

// Call this on /__/auth/handler to parse URL params after redirect
export const handleFacebookRedirectResult = async (onSuccess, onError) => {
    try {
        // Parse URL params
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const error = params.get("error");

        if (error) {
            console.error("[FB] OAuth error:", error);
            if (onError) onError(error);
            return;
        }

        if (!code) {
            console.log("[FB] No OAuth code found in URL");
            return;
        }

        console.log("[FB] OAuth code found:", code);
        if (onSuccess) onSuccess({ code });
    } catch (err) {
        console.error("[FB] Redirect result handling error:", err);
        if (onError) onError(err);
    }
};

export const handleFBsignInWithCredential = async (oauthCode, onSuccess, onError) => {
    try {
        // Get Access Token
        console.log(`[handleFBsignInWithCredential] got ouathCode: ${oauthCode}`);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/exchangeFBCode`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: oauthCode, redirectUri: window.location.origin + '/__/auth/handler' })
        });
        const data = await response.json();
        const accessToken = data?.access_token;
        console.log(`[handleFBsignInWithCredential] result: ${accessToken}`);
        if (!accessToken){
            throw new Error("No access token returned");
        }

        // Login with Firebase
        const credential = FacebookAuthProvider.credential(accessToken);
        const userCredential = await signInWithCredential(auth, credential);
        const user = userCredential.user;
        console.log("[handleFBsignInWithCredential] User signed in:", user);
        if (onSuccess) onSuccess(auth.currentUser);

    } catch (error) {
        console.error("Error signing in:", error);
        if (onError) onError(error);
    }
};

export const setFBProfileAsPhotoURL = async (accessToken) => {
    try {
        console.log("[updateFBProfile] Fetching real FB profile pic...");

        // Fetch the latest profile picture from the Facebook Graph API
        const fbResponse = await fetch(`https://graph.facebook.com/me?fields=picture.type(large)&access_token=${accessToken}`);
        const fbData = await fbResponse.json();
        const newPhotoUrl = fbData?.picture?.data?.url;

        if (!newPhotoUrl) {
            console.warn("[updateFBProfile] No real FB photo returned.");
            return null;
        }

        providerProfilePics.fb = newPhotoUrl;
        console.log("[updateFBProfile] Updating Firebase photoURL:", newPhotoUrl);

        const user = auth.currentUser;
        if (!user) {
            console.error("[updateFBProfile] No Firebase user found.");
            return null;
        }

        await updateProfile(user, { photoURL: newPhotoUrl });

        // Reload user so future calls have fresh data
        await user.reload();

        console.log("[updateFBProfile] Firebase photo updated successfully!");
        return auth.currentUser;

    } catch (err) {
        console.error("[updateFBProfile] Error:", err);
        return null;
    }
};
//=============================================

export const CheckLoggedIn = () => {
    return new Promise(resolve => {
        const unsubscribe = auth.onAuthStateChanged(async user => {
            unsubscribe(); // Immediately unsubscribe to avoid leaks
            if (user) {
                try {
                    await user.getIdToken(true);
                    console.log('[CheckLoggedIn] Token refreshed. UID:', user.uid);
                    resolve(true);
                } catch (error) {
                    console.error('[CheckLoggedIn] Error refreshing token:', error);
                    resolve(false);
                }
            } else {
                console.log('[CheckLoggedIn] User is logged out');
                resolve(false);
            }
        });
    });
};

export const isLoggedIn = () => {
    const loggedIn = auth.currentUser !== null;
    console.log('[isLoggedIn] User is signed in:', loggedIn);
    return loggedIn;
};

export const SignOutAndClearAllData = async () => {
    signOut(auth)
        .then(() => {
            // Sign-out successful.
            console.log('Signed out successfully');
            console.log(`Triggered ClearAllData`);
            localStorage.clear();
            window.location.reload();
        })
        .catch(error => {
            console.log('could not sign out');
            console.log(`Triggered ClearAllData`);
            localStorage.clear();
            window.location.reload();
        });
};

export const getValidIdToken = async () => {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken(true);
};

export const deleteAccount = async () => {
    try {
        await getValidIdToken();

        const user = auth.currentUser;
        if (user) {
            await deleteUser(user);
            localStorage.clear();
            window.location.reload();
            console.log('[deleteAccount] Account deleted successfully');
        } else {
            console.log('[deleteAccount] No user is currently signed in');
        }
    } catch (error) {
        console.error('[deleteAccount] Error deleting account:', error);
    }
};

export const linkAccount = async (error, onError) => {
    if (error.code === 'auth/account-exists-with-different-credential') {
        const customData = error.customData;
        const email = customData.email;
        const tokenResponse = customData._tokenResponse;
        const pendingCredential = FacebookAuthProvider.credential(tokenResponse.oauthAccessToken);

        console.log(`[signInWithFacebook] Error info: `, JSON.stringify(error));

        if (email) {
            console.log(`[signInWithFacebook] Retrieved email: `, email);
            // Fetch the sign-in methods for the email
            fetchSignInMethodsForEmail(auth, email).then(async methods => {
                console.log(`[signInWithFacebook] Sign-in methods: `, methods);

                if (methods.includes('password')) {
                    // User exists with email/password sign-in
                    console.log(
                        `[signInWithFacebook] Email linked to password. Prompting for login.`,
                    );
                    const password = prompt(
                        `An account with email ${email} exists. Please enter the password to link your Facebook account.`,
                    );
                    signInWithEmailAndPassword(auth, email, password)
                        .then(userCredential => {
                            console.log('Signed in with email/password.');
                            linkWithCredential(userCredential.user, pendingCredential)
                                .then(() => {
                                    console.log('Linked Facebook to existing account.');
                                })
                                .catch(linkError => {
                                    console.error('Error linking credential:', linkError);
                                    if (onError) onError(linkError);
                                });
                        })
                        .catch(signInError => {
                            console.error('Error signing in with email/password:', signInError);
                            if (onError) onError(signInError);
                        });
                } else if (methods.includes('facebook.com')) {
                    // Facebook is already linked
                    signInWithCredential(auth, pendingCredential)
                        .then(userCredential => {
                            console.log('Signed in with Facebook credential.');
                        })
                        .catch(signInError => {
                            console.error('Error signing in with Facebook:', signInError);
                            if (onError) onError(signInError);
                        });
                } else {
                    console.log(
                        `[signInWithFacebook] No matching sign-in methods found. Attempting fallback linking.`,
                    );
                    linkWithCredential(auth.currentUser, pendingCredential)
                        .then(() => {
                            console.log('Fallback linking succeeded.');
                        })
                        .catch(linkError => {
                            console.error('Error in fallback linking:', linkError);
                            if (onError) onError(linkError);
                        });
                }
            });
        } else {
            console.error('[signInWithFacebook] No email address available in the error object.');
        }
    } else {
        console.error('Error signing in:', error);
        if (onError) onError(error);
    }
};


export default app;
